import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { BarChart3, Eye, EyeOff, TrendingUp, DollarSign, Car, CheckCircle2, Loader2 } from "lucide-react";
import { ONBOARDING_DISMISSED_KEY } from "@/App";


export default function LoginPage() {
  const [mode, setMode] = useState<"login" | "register" | "forgot" | "forgot-username">("login");
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", companyName: "", email: "", referralCode: "" });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotUsernameEmail, setForgotUsernameEmail] = useState("");
  const [forgotUsernameSent, setForgotUsernameSent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { t } = useTranslation(["auth", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();

  useEffect(() => { setSubmitted(false); setTermsAccepted(false); }, [mode]);

  const mutation = useMutation({
    mutationFn: (data: typeof form & { termsAcceptedAt?: string }) =>
      apiRequest("POST", mode === "login" ? "/api/auth/login" : "/api/auth/register", data),
    onSuccess: () => {
      sessionStorage.removeItem(ONBOARDING_DISMISSED_KEY);
      qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
      window.location.href = "/";
    },
    onError: (err: any) => {
      toast({
        title: mode === "login" ? t("auth:login.toast.loginFailed") : t("auth:login.toast.registrationFailed"),
        description: err.message || t("auth:login.toast.checkCredentials"),
        variant: "destructive",
      });
    },
  });

  const forgotMutation = useMutation({
    mutationFn: (identifier: string) =>
      apiRequest("POST", "/api/auth/forgot-password", { identifier }),
    onSuccess: () => {
      setForgotSent(true);
    },
    onError: (err: any) => {
      toast({ title: t("common:error"), description: err.message ?? t("common:tryAgain"), variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (mode === "register") {
      if (!form.email || !form.password) {
        toast({ title: t("auth:login.toast.requiredFieldsMissing"), description: t("auth:login.toast.fillAllRequired"), variant: "destructive" });
        return;
      }
      if (!termsAccepted) {
        toast({ title: t("auth:login.toast.termsRequired"), description: t("auth:login.toast.acceptTermsMsg"), variant: "destructive" });
        return;
      }
      mutation.mutate({ ...form, username: form.email, termsAcceptedAt: new Date().toISOString() });
    } else {
      if (!form.username || !form.password) {
        toast({ title: t("auth:login.toast.requiredFieldsMissing"), description: t("auth:login.toast.fillAllFields"), variant: "destructive" });
        return;
      }
      mutation.mutate(form);
    }
  };

  const forgotUsernameMutation = useMutation({
    mutationFn: (email: string) =>
      apiRequest("POST", "/api/auth/forgot-username", { email }),
    onSuccess: () => {
      setForgotUsernameSent(true);
    },
    onError: (err: any) => {
      toast({ title: t("common:error"), description: err.message ?? t("common:tryAgain"), variant: "destructive" });
    },
  });

  const handleForgotSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotIdentifier.trim()) {
      toast({ title: t("auth:login.toast.required"), description: t("auth:login.toast.enterEmailOrUsername"), variant: "destructive" });
      return;
    }
    forgotMutation.mutate(forgotIdentifier.trim());
  };

  const handleForgotUsernameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotUsernameEmail.trim()) {
      toast({ title: t("auth:login.toast.required"), description: t("auth:login.toast.enterEmailAddress"), variant: "destructive" });
      return;
    }
    forgotUsernameMutation.mutate(forgotUsernameEmail.trim());
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex flex-1">
      <div className="hidden lg:flex lg:w-1/2 bg-primary flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-foreground/10 flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-primary-foreground">DealerMind</h1>
            <p className="text-primary-foreground/70 text-xs">{t("auth:brand.tagline")}</p>
          </div>
        </div>

        <div className="space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-primary-foreground leading-tight">
              {t("auth:login.hero.heading")}
            </h2>
            <p className="mt-3 text-primary-foreground/70 text-lg">
              {t("auth:login.hero.subheading")}
            </p>
          </div>

          <div className="grid gap-4">
            {[
              { icon: DollarSign, title: t("auth:login.hero.feature1Title"), desc: t("auth:login.hero.feature1Desc") },
              { icon: TrendingUp, title: t("auth:login.hero.feature2Title"), desc: t("auth:login.hero.feature2Desc") },
              { icon: Car, title: t("auth:login.hero.feature3Title"), desc: t("auth:login.hero.feature3Desc") },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4 p-4 rounded-lg bg-primary-foreground/5">
                <div className="w-9 h-9 rounded-md bg-primary-foreground/10 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <p className="font-semibold text-primary-foreground text-sm">{item.title}</p>
                  <p className="text-primary-foreground/60 text-xs mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-primary-foreground/40 text-xs">
          {t("auth:brand.copyright")}
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="text-xl font-bold">DealerMind</h1>
          </div>

          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl">
                {mode === "login" ? t("auth:login.titleLogin")
                  : mode === "register" ? t("auth:login.titleRegister")
                  : mode === "forgot" ? t("auth:login.titleForgot")
                  : t("auth:login.titleForgotUsername")}
              </CardTitle>
              <CardDescription>
                {mode === "login"
                  ? t("auth:login.descLogin")
                  : mode === "register"
                  ? t("auth:login.descRegister")
                  : mode === "forgot"
                  ? t("auth:login.descForgot")
                  : t("auth:login.descForgotUsername")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mode === "forgot" ? (
                forgotSent ? (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-chart-3/10 border border-chart-3/20">
                      <CheckCircle2 className="w-5 h-5 text-chart-3 shrink-0 mt-0.5" />
                      <p className="text-sm text-chart-3 font-medium">
                        {t("auth:login.forgotSentMessage")}
                      </p>
                    </div>
                    <Button
                      variant="outline" className="w-full"
                      onClick={() => { setMode("login"); setForgotSent(false); setForgotIdentifier(""); }}
                      data-testid="button-back-to-login-after-forgot"
                    >
                      {t("auth:login.backToSignIn")}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotSubmit} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="forgotIdentifier">{t("auth:login.emailOrUsername")}</Label>
                      <Input
                        id="forgotIdentifier"
                        placeholder={t("auth:login.emailOrUsernamePlaceholder")}
                        value={forgotIdentifier}
                        onChange={(e) => setForgotIdentifier(e.target.value)}
                        data-testid="input-forgot-identifier"
                        autoComplete="username email"
                        autoFocus
                      />
                    </div>
                    <Button
                      type="submit" className="w-full"
                      disabled={forgotMutation.isPending}
                      data-testid="button-submit-forgot"
                    >
                      {forgotMutation.isPending
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("auth:login.sending")}</>
                        : t("auth:login.sendResetLink")}
                    </Button>
                    <p className="text-center text-sm text-muted-foreground">
                      <button
                        type="button"
                        className="text-primary font-medium underline-offset-4 hover:underline"
                        onClick={() => setMode("login")}
                        data-testid="button-back-to-login"
                      >
                        {t("auth:login.backToSignIn")}
                      </button>
                    </p>
                  </form>
                )
              ) : mode === "forgot-username" ? (
                forgotUsernameSent ? (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-chart-3/10 border border-chart-3/20">
                      <CheckCircle2 className="w-5 h-5 text-chart-3 shrink-0 mt-0.5" />
                      <p className="text-sm text-chart-3 font-medium">
                        {t("auth:login.forgotUsernameSentMessage")}
                      </p>
                    </div>
                    <Button
                      variant="outline" className="w-full"
                      onClick={() => { setMode("login"); setForgotUsernameSent(false); setForgotUsernameEmail(""); }}
                      data-testid="button-back-to-login-after-forgot-username"
                    >
                      {t("auth:login.backToSignIn")}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotUsernameSubmit} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="forgotUsernameEmail">{t("auth:login.emailAddress")}</Label>
                      <Input
                        id="forgotUsernameEmail"
                        type="email"
                        placeholder={t("auth:login.emailPlaceholder")}
                        value={forgotUsernameEmail}
                        onChange={(e) => setForgotUsernameEmail(e.target.value)}
                        data-testid="input-forgot-username-email"
                        autoComplete="email"
                        autoFocus
                      />
                    </div>
                    <Button
                      type="submit" className="w-full"
                      disabled={forgotUsernameMutation.isPending}
                      data-testid="button-submit-forgot-username"
                    >
                      {forgotUsernameMutation.isPending
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("auth:login.sending")}</>
                        : t("auth:login.sendMyUsername")}
                    </Button>
                    <p className="text-center text-sm text-muted-foreground">
                      <button
                        type="button"
                        className="text-primary font-medium underline-offset-4 hover:underline"
                        onClick={() => setMode("login")}
                        data-testid="button-back-to-login-forgot-username"
                      >
                        {t("auth:login.backToSignIn")}
                      </button>
                    </p>
                  </form>
                )
              ) : (
              <>
              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === "register" ? (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="companyName">{t("auth:login.dealershipName")} <span className="text-muted-foreground text-xs font-normal">{t("auth:login.optional")}</span></Label>
                      <Input
                        id="companyName"
                        placeholder={t("auth:login.dealershipPlaceholder")}
                        value={form.companyName}
                        onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                        data-testid="input-company-name"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className={submitted && !form.email ? "text-destructive" : ""}>{t("auth:login.email")}</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder={t("auth:login.emailPlaceholder")}
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="input-email"
                        autoComplete="email"
                        required
                      />
                      <p className="text-xs text-muted-foreground">{t("auth:login.emailHint")}</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="referralCode">{t("auth:login.referralCode")} <span className="text-muted-foreground text-xs font-normal">{t("auth:login.optional")}</span></Label>
                      <Input
                        id="referralCode"
                        placeholder={t("auth:login.referralPlaceholder")}
                        value={form.referralCode}
                        onChange={(e) => setForm({ ...form, referralCode: e.target.value.toUpperCase() })}
                        data-testid="input-referral-code"
                        autoComplete="off"
                        maxLength={20}
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-1.5">
                    <Label htmlFor="username" className={submitted && !form.username ? "text-destructive" : ""}>{t("auth:login.emailOrUsername")}</Label>
                    <Input
                      id="username"
                      placeholder={t("auth:login.emailOrUsernamePlaceholder")}
                      value={form.username}
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                      data-testid="input-username"
                      autoComplete="username"
                    />
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="password" className={submitted && !form.password ? "text-destructive" : ""}>{t("auth:login.password")}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("auth:login.passwordPlaceholder")}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      data-testid="input-password"
                      autoComplete={mode === "login" ? "current-password" : "new-password"}
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="button-toggle-password"
                    >
                      {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>

                {mode === "register" && (
                  <div className="flex items-start gap-2.5 pt-1">
                    <Checkbox
                      id="terms-checkbox"
                      checked={termsAccepted}
                      onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                      data-testid="checkbox-terms"
                    />
                    <label
                      htmlFor="terms-checkbox"
                      className="text-sm text-muted-foreground leading-snug cursor-pointer"
                    >
                      {t("auth:login.agreeText")}{" "}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary font-medium underline-offset-4 hover:underline"
                        data-testid="link-terms-of-service"
                      >
                        {t("auth:login.termsOfService")}
                      </a>
                      {" "}{t("auth:login.and")}{" "}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary font-medium underline-offset-4 hover:underline"
                        data-testid="link-privacy-policy"
                      >
                        {t("auth:login.privacyPolicy")}
                      </a>
                    </label>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={mutation.isPending || (mode === "register" && !termsAccepted)}
                  data-testid="button-submit-auth"
                >
                  {mutation.isPending
                    ? (mode === "login" ? t("auth:login.signingIn") : t("auth:login.creatingAccount"))
                    : (mode === "login" ? t("auth:login.signIn") : t("auth:login.createAccountBtn"))}
                </Button>
                {mode === "login" && (
                  <div className="text-center mt-1 flex items-center justify-center gap-3">
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline-offset-4 hover:underline"
                      onClick={() => setMode("forgot")}
                      data-testid="button-forgot-password"
                    >
                      {t("auth:login.forgotPassword")}
                    </button>
                    <span className="text-xs text-muted-foreground/40">·</span>
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline-offset-4 hover:underline"
                      onClick={() => setMode("forgot-username")}
                      data-testid="button-forgot-username"
                    >
                      {t("auth:login.forgotUsernameBtn")}
                    </button>
                  </div>
                )}
              </form>

              <div className="mt-5 text-center text-sm text-muted-foreground">
                {mode === "login" ? (
                  <>
                    {t("auth:login.noAccount")}{" "}
                    <button
                      className="text-primary font-medium underline-offset-4 hover:underline"
                      onClick={() => setMode("register")}
                      data-testid="button-switch-to-register"
                    >
                      {t("auth:login.signUpNow")}
                    </button>
                  </>
                ) : (
                  <>
                    {t("auth:login.haveAccount")}{" "}
                    <button
                      className="text-primary font-medium underline-offset-4 hover:underline"
                      onClick={() => setMode("login")}
                      data-testid="button-switch-to-login"
                    >
                      {t("auth:login.signInSwitch")}
                    </button>
                  </>
                )}
              </div>
              </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      </div>

      <footer className="py-4 px-6 text-center border-t lg:border-t-0">
        <p className="text-xs text-muted-foreground/60">
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-muted-foreground transition-colors underline-offset-4 hover:underline"
            data-testid="footer-link-terms"
          >
            {t("auth:login.termsOfService")}
          </a>
          {" · "}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-muted-foreground transition-colors underline-offset-4 hover:underline"
            data-testid="footer-link-privacy"
          >
            {t("auth:login.privacyPolicy")}
          </a>
        </p>
      </footer>
    </div>
  );
}
